import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowForward } from "@mui/icons-material";

// Tile videos for the audience showcase
import faceCensor from "../../../videos/face_censor_example.mp4";
import marketExample from "../../../videos/marketer_example.mp4";
import podcastClimping from "../../../videos/podcast_clipping.mp4";
import launchVideo from "../../../videos/launch_video.mp4";
import AudienceCarousel from "./AudienceCarousel";

const audiences = [
  {
    title: "Podcasters & Creators",
    description:
      "Generate clips for YouTube, TikTok, or Instagram. Each clip is generated for virality and audience engagement.",
    video: podcastClimping,
  },
  {
    title: "Marketers & Advertisers",
    description:
      "Turn long-form videos into viral-ready clips optimized for TikTok, YouTube Shorts, and Instagram Reels.",
    video: marketExample,
  },
  {
    title: "Special Triggers",
    description:
      "Invoke triggers like 'blur out this man's face' on any video — described in plain English.",
    video: faceCensor,
  },
  {
    title: "Live captions & explainers",
    description:
      "Generate clean subtitles, highlights, and explainers from your video workflow.",
    video: launchVideo,
  },
];

const placeholders = [
  "Blur every face except mine in real time…",
  "Highlight the funny moments in my stream…",
  "Mute profanity from my guests automatically…",
  "Generate viral TikTok clips from this VOD…",
];

export default function Hero() {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState("");
  const [phIdx, setPhIdx] = useState(0);

  useEffect(() => {
    const id = setInterval(
      () => setPhIdx((i) => (i + 1) % placeholders.length),
      2800
    );

    return () => clearInterval(id);
  }, []);

  const handleSubmit = (e) => {
    e?.preventDefault?.();

    const q = prompt.trim();

    if (q) {
      navigate(`/login?intent=${encodeURIComponent(q)}`);
    } else {
      navigate("/login");
    }
  };

  const heroStructuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "SoftwareApplication",
        name: "Zenstream",
        applicationCategory: "VideoEditingApplication",
        operatingSystem: "Web, Windows, macOS",
        url: "https://zenstream.app/",
        description:
          "Zenstream is an AI-powered natural-language video effects creator. Describe the effect or trigger you want and Zenstream applies it — live and in real time.",
        offers: {
          "@type": "AggregateOffer",
          lowPrice: "0",
          highPrice: "79",
          priceCurrency: "USD",
        },
      },
      {
        "@type": "ItemList",
        name: "Built for every kind of video creator",
        itemListElement: audiences.map((m, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: m.title,
        })),
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>
          Zenstream | AI-Powered Natural Language Video Effects
        </title>

        <meta
          name="description"
          content="Zenstream is an AI-powered natural-language video effects creator. Describe the effect or trigger — blur faces, mute words, censor frames, generate clips — and Zenstream applies it in real time."
        />

        <meta
          name="keywords"
          content="natural language video effects, AI video editor, AI video triggers, edit video with text, livestream effects, blur faces, profanity muting, automatic clipping, AI content moderation"
        />

        <link rel="canonical" href="https://zenstream.app/" />
        <meta name="robots" content="index, follow, max-image-preview:large" />

        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://zenstream.app/" />
        <meta
          property="og:title"
          content="Zenstream | AI-Powered Natural Language Video Effects"
        />
        <meta
          property="og:description"
          content="Describe the video effect — Zenstream applies it. Live, in real time, with natural-language triggers."
        />
        <meta
          property="og:image"
          content="https://zenstream.app/Z_og_image.png"
        />

        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://zenstream.app/" />
        <meta
          name="twitter:title"
          content="Zenstream | AI-Powered Natural Language Video Effects"
        />
        <meta
          name="twitter:description"
          content="Describe the video effect — Zenstream applies it. Live, in real time, with natural-language triggers."
        />
        <meta
          name="twitter:image"
          content="https://zenstream.app/Z_twitter_image.png"
        />

        <script type="application/ld+json">
          {JSON.stringify(heroStructuredData)}
        </script>
      </Helmet>

      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.55 }}
        className="relative isolate w-full overflow-hidden bg-black pt-20 pb-24 sm:pt-28 sm:pb-32 md:pt-32 md:pb-40 font-display text-white"
        aria-label="Hero — AI-powered natural language video effects"
        itemScope
        itemType="https://schema.org/SoftwareApplication"
      >
        {/* Soft black-to-white transition only at the very bottom edge so
            the dark hero blends into the white SocialProof section below. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 -z-10 h-16 bg-gradient-to-b from-transparent to-white sm:h-20"
        />

        <div className="relative z-10 mx-auto flex w-full max-w-5xl flex-col items-center px-4 text-center sm:px-6 lg:px-8">
          {/* Eyebrow */}
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mb-6 text-sm font-semibold tracking-tight text-white"
          >
            Zenstream
          </motion.p>

          {/* Headline — white on black, mission-focused */}
          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.05 }}
            className="font-display max-w-5xl text-balance text-[2.25rem] font-medium leading-[1.05] tracking-[-0.035em] text-white xs:text-[2.5rem] sm:text-5xl md:text-6xl lg:text-7xl xl:text-[5.5rem]"
          >
            PR for your videos
            <br className="hidden sm:block" />
            <span className="sm:hidden"> </span>
            protecting your {" "}
            <em className="italic font-medium">brand</em>.
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.18 }}
            className="mt-6 max-w-2xl text-balance text-base leading-7 text-white/70 sm:text-lg md:text-xl"
          >
            You create the content and zenstream helps you stay
            <em className="italic">{" "}monetized</em>, keeping your brand safe.
          </motion.p>

          {/* Prompt CTA */}
          <motion.form
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            onSubmit={handleSubmit}
            role="search"
            aria-label="Try Zenstream — describe what you want to do with your video"
            className="mt-9 w-full max-w-3xl"
          >
            <div className="relative rounded-[2rem] border border-white/15 bg-white/5 p-2 backdrop-blur-md">
              <div className="flex w-full items-center gap-2 px-3 py-2 sm:px-4 sm:py-3">
                <label htmlFor="zen-hero-prompt" className="sr-only">
                  Describe what you want to do with your video
                </label>

                <input
                  id="zen-hero-prompt"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  type="text"
                  inputMode="text"
                  autoComplete="off"
                  placeholder={placeholders[phIdx]}
                  className="min-w-0 flex-1 border-none bg-transparent px-1 py-3 text-sm text-white outline-none placeholder:text-white/45 sm:text-base"
                />

                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-white px-4 py-2.5 text-sm font-semibold text-black transition hover:bg-neutral-200 sm:px-5 sm:text-base"
                >
                  <span className="hidden sm:inline">Try Zenstream</span>
                  <span className="sm:hidden">Try</span>

                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-black/10 transition group-hover:translate-x-0.5 group-hover:bg-black/20">
                    <ArrowForward className="!text-[16px]" />
                  </span>
                </motion.button>
              </div>
            </div>
          </motion.form>

          {/* Showcase intro */}
          <div className="mt-24 w-full sm:mt-32">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.38 }}
              className="mx-auto max-w-3xl text-center"
            >
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-white/70 sm:text-sm">
                One platform — every workflow
              </p>

              <h2 className="font-display mt-4 text-balance text-3xl font-medium tracking-tight text-white sm:text-4xl md:text-5xl">
                From first cut to <em className="italic font-medium">final cut</em>.
              </h2>

              <p className="mt-4 text-base leading-7 text-white/60 sm:text-lg">
                Clip, censor, caption, polish, publish — described in words.
              </p>
            </motion.div>
          </div>
        </div>

        {/* Carousel — full-width on the dark hero so neighbors peek
            cleanly off the edges, like a cinema reel. */}
        <div className="relative z-10 mt-10 w-full sm:mt-14">
          <AudienceCarousel audiences={audiences} />
        </div>
      </motion.section>
    </>
  );
}
